import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl, FormArray, AbstractControl } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TrainingService } from '../../../../services/training/training.service';
import { TrainingQuiz, TrainingQuizQuestion, QuizAnswer, QuizResult } from '../../../../services/training/training.interface';

@Component({
  selector: 'app-training-quiz',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatRadioModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './training-quiz.component.html',
  styleUrl: './training-quiz.component.css',
})
export class TrainingQuizComponent implements OnInit {
  @Input() moduleId = '';
  @Output() submitted = new EventEmitter<void>();

  quiz: TrainingQuiz | null = null;
  quizForm: FormGroup = new FormGroup({});
  loading = true;
  submitting = false;
  result: QuizResult | null = null;
  unansweredIndices = new Set<number>();

  constructor(private trainingService: TrainingService) {}

  ngOnInit(): void {
    this.loadQuiz();
  }

  loadQuiz(): void {
    this.loading = true;
    this.result = null;
    this.trainingService.getQuiz(this.moduleId).subscribe({
      next: (quiz) => {
        this.quiz = quiz;
        this.quizForm = this.buildForm(quiz);
        this.loading = false;
      },
      error: () => { this.loading = false; },
    });
  }

  buildForm(quiz: TrainingQuiz): FormGroup {
    const controls: Record<string, AbstractControl> = {};
    for (const q of quiz.questions) {
      if (q.type === 'multiple_choice' || q.type === 'true_false') {
        controls[q._id] = new FormControl<number | null>(null);
      } else if (q.type === 'multi_select') {
        controls[q._id] = new FormArray(q.options.map(() => new FormControl(false)));
      } else {
        controls[q._id] = new FormControl<string>('');
      }
    }
    return new FormGroup(controls);
  }

  getFormArray(questionId: string): FormArray {
    return this.quizForm.get(questionId) as FormArray;
  }

  getCheckboxControl(questionId: string, index: number): FormControl {
    return this.getFormArray(questionId).at(index) as FormControl;
  }

  submit(): void {
    if (!this.quiz) return;
    this.unansweredIndices = this.findUnansweredIndices();
    if (this.unansweredIndices.size > 0) return;
    this.submitting = true;
    const answers = this.collectAnswers(this.quiz);
    this.trainingService.submitQuiz(this.moduleId, answers).subscribe({
      next: (result) => {
        this.result = result;
        this.submitting = false;
        this.submitted.emit();
      },
      error: () => { this.submitting = false; },
    });
  }

  retake(): void {
    if (!this.quiz) return;
    this.quizForm = this.buildForm(this.quiz);
    this.result = null;
    this.unansweredIndices = new Set();
  }

  private findUnansweredIndices(): Set<number> {
    const indices = new Set<number>();
    if (!this.quiz) return indices;
    this.quiz.questions.forEach((q, qi) => {
      const val = this.quizForm.get(q._id)?.value;
      if (q.type === 'multiple_choice' || q.type === 'true_false') {
        if (val === null || val === undefined) indices.add(qi);
      } else if (q.type === 'multi_select') {
        if (!(val as boolean[]).some(v => v)) indices.add(qi);
      } else {
        if (!val || !(val as string).trim()) indices.add(qi);
      }
    });
    return indices;
  }

  collectAnswers(quiz: TrainingQuiz): QuizAnswer[] {
    return quiz.questions.map(q => {
      const val = this.quizForm.get(q._id)?.value;
      if (q.type === 'multiple_choice' || q.type === 'true_false') {
        return { questionId: q._id, selectedOptions: val != null ? [val] : [], freeText: '' };
      }
      if (q.type === 'multi_select') {
        const selected = (val as boolean[])
          .map((checked, i) => (checked ? i : -1))
          .filter(i => i >= 0);
        return { questionId: q._id, selectedOptions: selected, freeText: '' };
      }
      return { questionId: q._id, selectedOptions: [], freeText: val || '' };
    });
  }

  getQuestionResult(question: TrainingQuizQuestion) {
    return this.result?.answers.find(a => a.questionId === question._id) ?? null;
  }

  get unansweredLabel(): string {
    return Array.from(this.unansweredIndices).map(i => `Q${i + 1}`).join(', ');
  }

  hasFreeTextAnswers(): boolean {
    return this.result?.answers.some(a => a.requiresReview) ?? false;
  }
}
